import React, { CSSProperties } from "react";
import styled from "styled-components";
import fipayFonts from "../../theme/index";
import { Formik, Field, Form, ErrorMessage } from "formik";
import InfoCircles from "../../pages/CreatePaymentLink/InfoCircles";

interface InputProps {
  label: string;
  type: string;
  placeholder: string;
  value?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string | false | undefined;
  style?: CSSProperties;
  icon?: string;
  width?: string;
  height?: string;
  name?: string;
  className?: string;
  hasPrefix?: boolean;
  formik?: any;
  infoMessage?: string;
  info?: boolean;
  radioOptions?: { value: string; label: string }[];
  disabled?: boolean;
  hasBorder?: boolean;
  docName?: File | null | undefined;
  telInput?: boolean;
  maxLength?: number;
}

const InputContainer: React.FC<InputProps> = ({
  label,
  type,
  placeholder,
  value,
  onChange,
  error,
  style,
  icon,
  width,
  height,
  name,
  className,
  hasPrefix,
  formik,
  infoMessage,
  info,
  radioOptions,
  disabled,
  hasBorder,
  docName,
  telInput,
  maxLength,
}) => {
  return (
    <Container style={style} $width={width as string}>
      <Label htmlFor={label} $docName={docName !== null}>
        <LabelInfo>
          {label}
          {info && (
            <div className="info-circle">
              <InfoCircles message={infoMessage} />
            </div>
          )}
        </LabelInfo>
        {icon && <Icon src={icon} alt="" />}

        {hasBorder && (
          <span className="file-span">{docName?.name || placeholder}</span>
        )}
        {label === "Recipient's Phone Number" || telInput ? (
          <InputWrapper>
            <SmallInput type="text" placeholder="+1" disabled />
            <Input
              type={type}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              id={label}
              name={name}
              $hasPrefix={hasPrefix}
              className={className}
              error={error}
            />
          </InputWrapper>
        ) : (
          <MainInputWraper $hasBorder={hasBorder}>
            <Input
              type={type}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              id={label}
              name={name}
              $hasPrefix={hasPrefix}
              className={className}
              error={error}
              disabled={disabled}
              $disabled={disabled}
              maxLength={maxLength}
            />
          </MainInputWraper>
        )}
      </Label>

      {value && hasPrefix && (
        <span>{placeholder.length > 1 ? placeholder[0] : placeholder}</span>
      )}

      {name && <StyledErrorText name={name} component="div" />}
    </Container>
  );
};

const Container = styled.div<{ $width: string }>`
  margin-bottom: 5px;
  margin-right: 5px;
  position: relative;
  width: ${({ $width }) => $width || "100%"};

  span {
    position: absolute;
    left: 1rem;
    top: 51%;
  }
  @media only screen and (max-width: 767px) {
    width: 100%;
  }
`;

export const LabelInfo = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  .info-circle {
    cursor: pointer;
  }
`;

export const Label = styled.label<{ $docName?: boolean }>`
  display: flex;
  font-weight: ${fipayFonts.fipayFonts.fontWeight.medium};
  font-family: "Gt Walsheim Pro", sans-serif;
  font-size: ${fipayFonts.fipayFonts.fontSize.body3};
  letter-spacing: 2%;
  color: #0e0e0e;
  flex-shrink: 0;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;

  .file-span {
    margin-left: 8px;
    color: ${({ $docName }) => ($docName ? "#0e0e0e" : " #b9b8b8")};
    font-size: ${fipayFonts.fipayFonts.fontSize.body2};
    opacity: 0.6;
    position: absolute;
    left: 0.2rem;
  }
`;

const Icon = styled.img`
  position: absolute;
  right: 4%;
  top: 50%;
  z-index: 100;
  height: 1.3rem;
  width: 1.3rem;
`;

const Input = styled.input<{
  $hasPrefix?: boolean;
  error?: string | false | undefined;
  $disabled?: boolean;
}>`
  width: 100%;
  height: ${(props) => props.height || "50px"};
  text-indent: 10px;
  padding: 8px 8px 8px ${({ $hasPrefix }) => ($hasPrefix ? 19 : 8)}px;
  border: 1px solid ${({ error }) => (error ? "#B00020" : "#f0f2f4")};
  border-radius: 15px;
  overflow: auto;
  font-weight: ${fipayFonts.fipayFonts.fontWeight.regular};
  font-size: ${fipayFonts.fipayFonts.fontSize.body2};
  position: relative;
  color: ${({ error }) => (error ? "#B00020" : "#0e0e0e")};
  box-sizing: border-box;
  background: ${({ error, $disabled }) =>
    error ? "#FED8D880" : $disabled ? "#F7F7F7" : "transparent"};

  &::placeholder {
    color: #b9b8b8;
    font-size: ${fipayFonts.fipayFonts.fontSize.body2};
    opacity: 0.6;
    position: absolute;
    left: 0.2rem;
  }

  &:focus {
    border: 1px solid #3f4188;
    outline: none;
    background-color: #e9eaf9;
  }
`;

export const LabelAndInfo = styled.div`
  display: flex;
  align-items: center;
`;

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const SmallInput = styled.input`
  width: 60px;
  border: 1px solid #f0f2f4;
  border-radius: 15px;
  padding: 8px;
  margin-right: 10px;

  &::placeholder {
    color: #b9b8b8;
    font-size: ${fipayFonts.fipayFonts.fontSize.body2};
  }
`;
export const MainInputWraper = styled.div<{
  error?: string | false | undefined;
  $hasBorder?: boolean;
}>`
  border: 1px solid
    ${({ error, $hasBorder }) =>
      error ? "#B00020" : $hasBorder ? "#f0f2f4" : "transparent"};
  border-radius: 15px;
  width: 100%;
`;
export default InputContainer;

export const StyledErrorText = styled(ErrorMessage)`
  color: #b00020;
`;
